import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Overlay, Popover, Spinner } from 'react-bootstrap';
import {
  getNotificationPreferencesRequest,
  updateNotificationPreferencesRequest,
} from '../../api/community';
import {
  createNotification,
  NOTIFICATION_TYPE,
} from '../../services/Notification';

const NOTIFICATION_SECTIONS = [
  {
    id: 'community',
    title: 'Community',
    description: 'Notifications for when users like and reply to your posts.',
    items: [
      { id: 'likesOnPosts', label: 'Likes on your posts' },
      { id: 'repliesToPosts', label: 'Replies to your posts' },
    ],
  },
  {
    id: 'coursesPlans',
    title: 'Courses & Plans',
    description: 'Get notified about new courses, plans, and updates.',
    items: [
      { id: 'therapyPlanUpdates', label: 'Therapy plan updates' },
      { id: 'newPremiumCourses', label: 'New premium courses' },
    ],
  },
  {
    id: 'accountability',
    title: 'Accountability',
    description:
      "Get reminders if you haven't made progress on an active plan.",
    items: [{ id: 'reminders', label: 'Reminders' }],
  },
];

const DEFAULT_PREFERENCES = {
  likesOnPosts: { email: false, push: false },
  repliesToPosts: { email: false, push: false },
  therapyPlanUpdates: { email: false, push: false },
  newPremiumCourses: { email: false, push: false },
  reminders: { email: false, push: false },
};

const mapApiToPreferences = data => ({
  likesOnPosts: {
    push: data.notify_likes_by_push,
    email: data.notify_likes_by_email,
  },
  repliesToPosts: {
    push: data.notify_replies_by_push,
    email: data.notify_replies_by_email,
  },
  therapyPlanUpdates: {
    push: data.notify_plans_update_by_push,
    email: data.notify_plans_update_by_email,
  },
  newPremiumCourses: {
    push: data.notify_new_premium_courses_by_push,
    email: data.notify_new_premium_courses_by_email,
  },
  reminders: {
    push: data.notify_reminder_by_push,
    email: data.notify_reminder_by_email,
  },
});

const mapPreferencesToApi = preferences => ({
  notify_likes_by_push: preferences.likesOnPosts.push,
  notify_likes_by_email: preferences.likesOnPosts.email,
  notify_replies_by_push: preferences.repliesToPosts.push,
  notify_replies_by_email: preferences.repliesToPosts.email,
  notify_plans_update_by_push: preferences.therapyPlanUpdates.push,
  notify_plans_update_by_email: preferences.therapyPlanUpdates.email,
  notify_new_premium_courses_by_push: preferences.newPremiumCourses.push,
  notify_new_premium_courses_by_email: preferences.newPremiumCourses.email,
  notify_reminder_by_push: preferences.reminders.push,
  notify_reminder_by_email: preferences.reminders.email,
});

const getStatusText = ({ email, push }) => {
  if (!email && !push) return 'Off';

  const channels = [];
  if (push) channels.push('Push');
  if (email) channels.push('Email');
  return `On: ${channels.join(', ')}`;
};

const ToggleSwitch = ({ checked, onChange, id, label, isLoading }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-busy={isLoading}
    aria-label={label}
    id={id}
    disabled={isLoading}
    className={`notifications-toggle${
      checked ? ' notifications-toggle--on' : ''
    }${isLoading ? ' notifications-toggle--loading' : ''}`}
    onClick={event => {
      event.stopPropagation();
      onChange(!checked);
    }}
  >
    <span className="notifications-toggle-handle" />
  </button>
);

ToggleSwitch.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
};

const NotificationRow = ({ item, preferences, onToggle, savingToggleKeys }) => {
  const [show, setShow] = useState(false);
  const targetRef = useRef(null);
  const setting = preferences[item.id];

  const handleHide = () => setShow(false);

  const handleEditClick = () => {
    setShow(prev => !prev);
  };

  const handleToggle = key => {
    onToggle(item.id, key);
  };

  return (
    <div className="notifications-row">
      <div className="notifications-row-info">
        <p className="notifications-row-title">{item.label}</p>
        <p className="notifications-row-status">{getStatusText(setting)}</p>
      </div>
      <button
        ref={targetRef}
        type="button"
        className="notifications-edit-btn"
        onClick={handleEditClick}
      >
        Edit
      </button>
      <Overlay
        onHide={handleHide}
        placement="bottom-end"
        rootClose
        transition={false}
        show={show}
        target={targetRef.current}
      >
        <Popover bsPrefix="notifications-popover">
          <Popover.Body className="notifications-popover-content">
            <div
              className={`notifications-popover-item${
                savingToggleKeys.has(`${item.id}-email`)
                  ? ' notifications-popover-item--loading'
                  : ''
              }`}
            >
              <span>Email updates</span>
              <ToggleSwitch
                id={`${item.id}-email`}
                label="Email updates"
                checked={setting.email}
                isLoading={savingToggleKeys.has(`${item.id}-email`)}
                onChange={() => handleToggle('email')}
              />
            </div>
            <div
              className={`notifications-popover-item${
                savingToggleKeys.has(`${item.id}-push`)
                  ? ' notifications-popover-item--loading'
                  : ''
              }`}
            >
              <span>Push notification</span>
              <ToggleSwitch
                id={`${item.id}-push`}
                label="Push notification"
                checked={setting.push}
                isLoading={savingToggleKeys.has(`${item.id}-push`)}
                onChange={() => handleToggle('push')}
              />
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
    </div>
  );
};

NotificationRow.propTypes = {
  item: PropTypes.object.isRequired,
  preferences: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
  savingToggleKeys: PropTypes.instanceOf(Set).isRequired,
};

const Notifications = () => {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);
  const [savingToggleKeys, setSavingToggleKeys] = useState(() => new Set());
  const preferencesRef = useRef(DEFAULT_PREFERENCES);
  const saveVersionRef = useRef(0);

  useEffect(() => {
    getNotificationPreferencesRequest()
      .then(response => {
        const nextPreferences = mapApiToPreferences(response.data);
        preferencesRef.current = nextPreferences;
        setPreferences(nextPreferences);
      })
      .catch(() => {
        createNotification({
          type: NOTIFICATION_TYPE.ERROR,
          message: 'Unable to load notification preferences.',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleToggle = (itemId, key) => {
    const toggleKey = `${itemId}-${key}`;
    const current = preferencesRef.current;
    const previousValue = current[itemId][key];
    const nextPreferences = {
      ...current,
      [itemId]: {
        ...current[itemId],
        [key]: !current[itemId][key],
      },
    };

    preferencesRef.current = nextPreferences;
    setPreferences(nextPreferences);
    setSavingToggleKeys(prev => new Set(prev).add(toggleKey));

    const requestVersion = ++saveVersionRef.current;

    updateNotificationPreferencesRequest(mapPreferencesToApi(nextPreferences))
      .then(response => {
        if (requestVersion !== saveVersionRef.current) return;

        const syncedPreferences = mapApiToPreferences(response.data);
        preferencesRef.current = syncedPreferences;
        setPreferences(syncedPreferences);
      })
      .catch(() => {
        if (requestVersion !== saveVersionRef.current) return;

        const revertedPreferences = {
          ...preferencesRef.current,
          [itemId]: {
            ...preferencesRef.current[itemId],
            [key]: previousValue,
          },
        };

        preferencesRef.current = revertedPreferences;
        setPreferences(revertedPreferences);
        createNotification({
          type: NOTIFICATION_TYPE.ERROR,
          message: 'Unable to save notification preferences.',
        });
      })
      .finally(() => {
        setSavingToggleKeys(prev => {
          const next = new Set(prev);
          next.delete(toggleKey);
          return next;
        });
      });
  };

  return (
    <div className="profile-form-div-outer">
      <div className="profile-form-div account-settings notifications-page">
        <h2 className="account-settings-title">Notifications</h2>

        {isLoading ? (
          <div className="notifications-loading">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {NOTIFICATION_SECTIONS.map(section => (
              <section className="notifications-section" key={section.id}>
                <h3 className="notifications-section-title">{section.title}</h3>
                <p className="notifications-section-description">
                  {section.description}
                </p>
                <div className="notifications-section-card">
                  {section.items.map(item => (
                    <NotificationRow
                      key={item.id}
                      item={item}
                      preferences={preferences}
                      onToggle={handleToggle}
                      savingToggleKeys={savingToggleKeys}
                    />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default Notifications;
